import { Combobox, Input, Pill, PillsInput, ScrollArea, Stack, useCombobox } from "@mantine/core";
import { useUncontrolled } from "@mantine/hooks";
import { useId, useState } from "react";

export type ChipMultiSelectItem = {
  value: string;
  label: string;
};

type ChipMultiSelectProps = {
  items: ChipMultiSelectItem[];
  value?: string[];
  defaultValue?: string[];
  onChange: (selected: string[]) => void;
  inputLabel: string;
  inputPlaceholder?: string;
  w?: string;
  disabled?: boolean;
  error?: string[] | undefined;
};

export function ChipMultiSelect({
  items,
  disabled = false,
  value = [],
  defaultValue,
  onChange,
  inputLabel,
  inputPlaceholder = "Suche",
  w,
  error,
}: ChipMultiSelectProps) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });
  const uuid = useId();

  const [search, setSearch] = useState("");
  const [_value, setValue] = useUncontrolled({
    value,
    defaultValue,
    finalValue: [],
    onChange,
  });

  const values = _value.map((v) => (
    <Pill disabled={disabled} size="lg" key={v} withRemoveButton onRemove={() => removeValue(v)}>
      {items.find((item) => item.value === v)?.label}
    </Pill>
  ));

  const options = items
    .filter((item) => !_value.includes(item.value))
    .filter((item) => item.label.toLowerCase().includes(search.trim().toLowerCase()))
    .map((item) => (
      <Combobox.Option value={item.value} key={item.value} onClick={() => combobox.closeDropdown()}>
        <span>{item.label}</span>
      </Combobox.Option>
    ));

  const addValue = (v: string) => setValue([..._value, v]);
  const removeValue = (v: string) => setValue(_value.filter((val) => val !== v));

  return (
    <Stack mih="6.5rem">
      <div>
        <Input.Label htmlFor={uuid}>{inputLabel}</Input.Label>
        <Combobox disabled={disabled} width={w} store={combobox} onOptionSubmit={addValue} withinPortal={false}>
          <Combobox.DropdownTarget>
            <PillsInput id={uuid} error={error} disabled={disabled} w={w} onClick={() => combobox.openDropdown()}>
              <Combobox.EventsTarget>
                <PillsInput.Field
                  onBlur={() => combobox.closeDropdown()}
                  onFocus={() => combobox.openDropdown()}
                  placeholder={inputPlaceholder}
                  onChange={(event) => {
                    combobox.updateSelectedOptionIndex();
                    setSearch(event.currentTarget.value);
                  }}
                  value={search}
                />
              </Combobox.EventsTarget>
            </PillsInput>
          </Combobox.DropdownTarget>
          <Combobox.Dropdown>
            <ScrollArea.Autosize type="scroll" mah="15rem">
              <Combobox.Options>{options.length > 0 ? options : <Combobox.Empty>Keine Resultate...</Combobox.Empty>}</Combobox.Options>
            </ScrollArea.Autosize>
          </Combobox.Dropdown>
        </Combobox>
      </div>
      <Pill.Group>{values}</Pill.Group>
    </Stack>
  );
}
